import { DomHelpers } from "@/utils/DomHelpers";
import { Logger } from "@/utils/Logger";
import {
    MusicVideoItem,
    type MusicVideoItemData,
} from "@/home/work/MusicVideoItem";

const STORAGE_KEY = "815video_mv_list_v1";
const FROM_DETAIL_KEY = "815video_mv_from_detail";
const STORAGE_VERSION = 2;
/** 뷰포트 하단에서 이만큼 여유 있을 때 다음 페이지 요청 */
const SENTINEL_ROOT_MARGIN = "0px 0px 520px 0px";
/** 이 이상 세로 스크롤이 생기면 “바닥 근접” IO에만 맡김 */
const SCROLLABLE_EXCESS_PX = 48;
/** 짧은 페이지 자동 연속 로드 상한 (비정상 API 대비) */
const MAX_AUTO_FILL_PAGES = 80;

interface PersistShape {
    v: number;
    /** 현재 목록의 API 베이스 URL (복원 시 페이지와 일치할 때만 복원) */
    apiUrl: string;
    items: MusicVideoItemData[];
    nextCursor: string | null;
    hasMore: boolean;
    scrollY: number;
}

interface ApiResponse {
    ok?: boolean;
    items?: MusicVideoItemData[];
    nextCursor?: string | null;
    hasMore?: boolean;
    error?: string;
}

/** PerformanceNavigationTiming.type — 브라우저 back/forward 시에만 'back_forward' */
type LenisLike = {
    scroll?: number;
    limit?: number;
    on?: (ev: string, fn: () => void) => void;
};

function getLenis(): LenisLike | undefined {
    return (window as unknown as { lenis?: LenisLike }).lenis;
}

function isBackForwardNavigation(): boolean {
    try {
        const entries = performance.getEntriesByType?.("navigation");
        const nav = entries?.[0] as { type?: string } | undefined;
        return nav?.type === "back_forward";
    } catch {
        return false;
    }
}

function isFromDetailPage(): boolean {
    try {
        return sessionStorage.getItem(FROM_DETAIL_KEY) === "1";
    } catch {
        return false;
    }
}

function clearFromDetailFlag(): void {
    try {
        sessionStorage.removeItem(FROM_DETAIL_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * 뮤직비디오 목록.
 * - 상세에서 뒤로가기로 돌아온 경우에만 스크롤·목록 복원.
 * - 리스트에서 새로고침/직접 진입 시에는 저장소 비우고 맨 위(0)에서 처음부터 로드.
 * - 하단 근접 시 자동 fetch. 첫 화면에 스크롤이 안 생기면 다음 페이지를 자동 연속 로드.
 */
export class MusicVideoList {
    private readonly root: HTMLElement;
    private readonly apiUrl: string;
    private readonly pageLimit: number;
    private readonly gridEl: HTMLElement;
    private readonly sentinelEl: HTMLElement;
    private readonly loaderEl: HTMLElement;
    private readonly emptyEl: HTMLElement;
    private readonly endEl: HTMLElement;
    private readonly errorEl: HTMLElement;
    private readonly retryBtn: HTMLButtonElement;
    private readonly emptyMsg: string;
    private readonly errorMsg: string;
    private readonly endMsg: string;

    private items: MusicVideoItemData[] = [];
    private nextCursor: string | null = null;
    private hasMore = true;
    private loading = false;
    private initialDone = false;
    private fetchError = false;
    private io: IntersectionObserver | null = null;
    private autoFillChain = 0;
    /** 상세에서 뒤로가기/Back to list로 복원된 경우에만 true. 이때만 스크롤 없을 때 자동 추가 로드. */
    private restoredFromStorage = false;
    private readonly onScrollNearBottomThrottled: () => void;

    constructor(root: HTMLElement) {
        this.root = root;
        this.apiUrl = (root.getAttribute("data-api-url") ?? "").trim();
        this.pageLimit = this.parsePageLimit(root.getAttribute("data-limit"));

        const refs = this.resolveDOMElements();
        this.gridEl = refs.grid;
        this.sentinelEl = refs.sentinel;
        this.loaderEl = refs.loader;
        this.emptyEl = refs.empty;
        this.endEl = refs.end;
        this.errorEl = refs.error;
        this.retryBtn = refs.retry;

        this.emptyMsg = root.getAttribute("data-empty-msg") ?? "";
        this.errorMsg = root.getAttribute("data-error-msg") ?? "";
        this.endMsg = root.getAttribute("data-end-msg") ?? "END OF LIST";

        this.onScrollNearBottomThrottled = DomHelpers.throttle(
            () => this.checkScrollNearBottom(),
            100,
        );
    }

    private parsePageLimit(attr: string | null): number {
        const n = parseInt(attr ?? "9", 10);
        return Math.min(24, Math.max(1, Number.isNaN(n) ? 9 : n));
    }

    private resolveDOMElements(): {
        grid: HTMLElement;
        sentinel: HTMLElement;
        loader: HTMLElement;
        empty: HTMLElement;
        end: HTMLElement;
        error: HTMLElement;
        retry: HTMLButtonElement;
    } {
        const grid = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-grid]");
        const sentinel = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-sentinel]");
        const loader = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-loader]");
        const empty = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-empty]");
        const end = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-end]");
        const err = DomHelpers.qs<HTMLElement>(this.root, "[data-mv-error]");
        const retry = DomHelpers.qs<HTMLButtonElement>(this.root, "[data-mv-retry]");

        if (!grid || !sentinel || !loader || !empty || !end || !err || !retry) {
            throw new Error("MusicVideoList: missing DOM hooks");
        }
        return { grid, sentinel, loader, empty, end, error: err, retry };
    }

    init(): void {
        if (!this.apiUrl) {
            this.showError("API URL missing");
            return;
        }

        const fromBack = isBackForwardNavigation();
        const fromDetailLink = isFromDetailPage();
        const shouldTryRestore = fromBack || fromDetailLink;
        let restored = false;

        if (shouldTryRestore) {
            restored = this.tryRestoreFromStorage();
            this.restoredFromStorage = restored;
            clearFromDetailFlag();
        } else {
            this.clearStoredState();
            this.scrollToTop();
            this.restoredFromStorage = false;
        }

        if (!restored) {
            void this.fetchPage(null);
        } else {
            this.updateFooterUi();
            if (restored && this.hasMore && this.items.length > 0) {
                this.scheduleAutoFillIfShortPage();
            }
        }

        if (this.shouldObserveSentinel(restored)) {
            this.setupInfiniteScroll();
        }
        this.bindScrollNearBottom();

        this.bindRetry();
        this.bindLinkClickPersist();
    }

    /**
     * Lenis는 window scroll 이벤트를 거의 쓰지 않음 → lenis.on('scroll') + 네이티브 폴백.
     */
    private bindScrollNearBottom(): void {
        const lenis = getLenis();
        if (typeof lenis?.on === "function") {
            lenis.on("scroll", this.onScrollNearBottomThrottled);
        }
        window.addEventListener("scroll", this.onScrollNearBottomThrottled, {
            passive: true,
        });
        window.addEventListener("resize", this.onScrollNearBottomThrottled);
    }

    private getScrollTopNow(): number {
        const lenis = getLenis();
        if (lenis != null && typeof lenis.scroll === "number") {
            return lenis.scroll;
        }
        return window.scrollY ?? document.documentElement.scrollTop ?? 0;
    }

    private getScrollMax(): number {
        const lenis = getLenis();
        if (lenis != null && typeof lenis.limit === "number") {
            return Math.max(0, lenis.limit);
        }
        const doc = document.documentElement;
        return Math.max(0, doc.scrollHeight - window.innerHeight);
    }

    /** 바닥에서 600px 이내면 다음 페이지 (Lenis·네이티브 공통) */
    private checkScrollNearBottom(): void {
        const max = this.getScrollMax();
        const top = this.getScrollTopNow();
        if (max <= 0) return;
        if (max - top <= 600) {
            this.onNearBottom();
        }
    }

    /** 상세로 갈 때만 스크롤 위치 저장 갱신 */
    private bindLinkClickPersist(): void {
        this.root.addEventListener("click", (e) => {
            const t = e.target as HTMLElement;
            if (t.closest(".video-grid__link")) {
                this.persistScrollOnly();
            }
        });
    }

    private bindRetry(): void {
        this.retryBtn.addEventListener("click", () => {
            this.fetchError = false;
            this.errorEl.hidden = true;
            this.retryBtn.hidden = true;
            void this.fetchPage(
                this.items.length === 0 ? null : this.nextCursor,
            );
        });
    }

    private shouldObserveSentinel(restored: boolean): boolean {
        if (restored) {
            return this.items.length > 0 && this.hasMore;
        }
        return true;
    }

    private scrollToTop(): void {
        window.scrollTo(0, 0);
    }

    private clearStoredState(): void {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            Logger.debug("MusicVideoList clearStoredState failed", e);
        }
    }

    private setupInfiniteScroll(): void {
        this.io = new IntersectionObserver(
            (entries) => {
                const e = entries[0];
                if (!e?.isIntersecting) return;
                this.onNearBottom();
            },
            {
                root: null,
                rootMargin: SENTINEL_ROOT_MARGIN,
                threshold: 0,
            },
        );
        this.io.observe(this.sentinelEl);
    }

    private stopInfiniteScroll(): void {
        this.io?.disconnect();
        this.io = null;
    }

    private onNearBottom(): void {
        if (this.loading || !this.hasMore || this.fetchError) return;
        if (!this.initialDone || this.items.length === 0) return;
        if (this.nextCursor === null) {
            this.hasMore = false;
            this.updateFooterUi();
            this.stopInfiniteScroll();
            return;
        }
        this.autoFillChain = 0;
        void this.fetchPage(this.nextCursor);
    }

    /** 문서 높이가 뷰포트보다 충분히 크면 스크롤로 하단 로드 가능 */
    private isPageScrollable(): boolean {
        return this.getScrollMax() > SCROLLABLE_EXCESS_PX;
    }

    private scheduleAutoFillIfShortPage(): void {
        if (!this.restoredFromStorage) return;
        if (!this.hasMore || this.fetchError || this.items.length === 0) return;
        if (this.nextCursor == null) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                void this.autoFillUntilScrollable();
            });
        });
    }

    /**
     * 스크롤이 생기기 전에는 IO가 다시 안 불리므로, 짧은 페이지에서 다음 페이지를 연속 요청.
     */
    private async autoFillUntilScrollable(): Promise<void> {
        if (this.loading || !this.hasMore || this.fetchError) return;
        if (this.items.length === 0) return;
        if (this.isPageScrollable()) return;
        if (this.nextCursor == null) return;
        if (this.autoFillChain >= MAX_AUTO_FILL_PAGES) {
            Logger.debug("MusicVideoList autoFill cap reached");
            return;
        }
        this.autoFillChain += 1;
        await this.fetchPage(this.nextCursor);
    }

    private tryRestoreFromStorage(): boolean {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return false;

            const data = JSON.parse(raw) as PersistShape;
            if (
                data.v !== STORAGE_VERSION ||
                typeof data.apiUrl !== "string" ||
                data.apiUrl !== this.apiUrl ||
                !Array.isArray(data.items) ||
                data.items.length === 0
            ) {
                this.clearStoredState();
                return false;
            }

            this.items = data.items;
            this.nextCursor = data.nextCursor ?? null;
            this.hasMore = Boolean(data.hasMore);
            this.initialDone = true;

            for (const item of this.items) {
                this.gridEl.appendChild(MusicVideoItem.createLi(item));
            }

            const scrollY =
                typeof data.scrollY === "number" ? data.scrollY : 0;
            requestAnimationFrame(() => {
                const lenis = getLenis();
                if (lenis != null && typeof (lenis as { scrollTo?: (y: number, opts?: { immediate?: boolean }) => void }).scrollTo === "function") {
                    (lenis as { scrollTo: (y: number, opts?: { immediate?: boolean }) => void }).scrollTo(scrollY, { immediate: true });
                } else {
                    window.scrollTo(0, scrollY);
                }
            });
            return true;
        } catch (e) {
            Logger.debug("MusicVideoList restore failed", e);
            this.clearStoredState();
            return false;
        }
    }

    private persist(): void {
        try {
            const payload: PersistShape = {
                v: STORAGE_VERSION,
                apiUrl: this.apiUrl,
                items: this.items,
                nextCursor: this.nextCursor,
                hasMore: this.hasMore,
                scrollY: this.getScrollTopNow(),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (e) {
            Logger.debug("MusicVideoList persist failed", e);
        }
    }

    private persistScrollOnly(): void {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const data = JSON.parse(raw) as PersistShape;
            data.scrollY = this.getScrollTopNow();
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {
            /* ignore */
        }
    }

    private setLoading(on: boolean): void {
        this.loading = on;
        this.loaderEl.hidden = !on;
        this.loaderEl.setAttribute("aria-busy", on ? "true" : "false");
    }

    private async fetchPage(cursor: string | null): Promise<void> {
        if (this.loading) return;

        if (cursor === null && this.items.length > 0 && this.initialDone) {
            return;
        }
        if (cursor !== null) {
            if (!this.hasMore || this.items.length === 0) return;
            if (this.nextCursor === null) {
                this.hasMore = false;
                this.updateFooterUi();
                this.stopInfiniteScroll();
                return;
            }
        }

        this.setLoading(true);
        this.emptyEl.hidden = true;

        const url = this.buildPageUrl(cursor);
        try {
            const data = await this.requestPage(url);
            this.applyPageResponse(data, cursor);
        } catch (e) {
            Logger.error("MusicVideoList fetch failed", e);
            this.fetchError = true;
            this.showError(this.errorMsg);
        } finally {
            this.setLoading(false);
            if (
                this.restoredFromStorage &&
                !this.fetchError &&
                this.hasMore &&
                this.items.length > 0 &&
                this.nextCursor != null
            ) {
                this.scheduleAutoFillIfShortPage();
            }
        }
    }

    private buildPageUrl(cursor: string | null): string {
        const params = new URLSearchParams();
        params.set("limit", String(this.pageLimit));
        if (cursor) params.set("cursor", cursor);
        const sep = this.apiUrl.includes("?") ? "&" : "?";
        return `${this.apiUrl}${sep}${params.toString()}`;
    }

    private async requestPage(url: string): Promise<ApiResponse> {
        const res = await fetch(url, {
            method: "GET",
            headers: { Accept: "application/json" },
            credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ApiResponse;
        if (!data.ok || !Array.isArray(data.items)) {
            throw new Error(data.error ?? "bad_response");
        }
        return data;
    }

    private applyPageResponse(data: ApiResponse, cursor: string | null): void {
        this.fetchError = false;
        this.errorEl.hidden = true;
        this.retryBtn.hidden = true;

        if (data.items!.length === 0 && this.items.length === 0) {
            this.handleEmptyList(data);
            return;
        }

        if (cursor !== null && data.items!.length === 0) {
            this.hasMore = false;
            this.nextCursor = null;
            this.initialDone = true;
            this.persist();
            this.updateFooterUi();
            this.stopInfiniteScroll();
            return;
        }

        this.appendItems(data.items!);
        this.applyCursorAndHasMore(data);

        this.initialDone = true;
        this.persist();
        this.updateFooterUi();

        if (!this.hasMore) {
            this.stopInfiniteScroll();
        }
    }

    private handleEmptyList(_data: ApiResponse): void {
        this.emptyEl.textContent = this.emptyMsg;
        this.emptyEl.hidden = false;
        this.hasMore = false;
        this.nextCursor = null;
        this.initialDone = true;
        this.updateFooterUi();
        this.stopInfiniteScroll();
        this.clearStoredState();
    }

    private appendItems(rows: MusicVideoItemData[]): void {
        for (const row of rows) {
            const item: MusicVideoItemData = {
                id: row.id,
                title: String(row.title ?? ""),
                thumb: row.thumb ? String(row.thumb) : null,
                href: String(row.href ?? "#"),
            };
            this.items.push(item);
            this.gridEl.appendChild(MusicVideoItem.createLi(item));
        }
    }

    private applyCursorAndHasMore(data: ApiResponse): void {
        const rawNext =
            data.nextCursor != null && String(data.nextCursor).length > 0
                ? String(data.nextCursor)
                : null;

        if (data.hasMore === false || rawNext === null) {
            this.hasMore = false;
            this.nextCursor = null;
        } else {
            this.hasMore = true;
            this.nextCursor = rawNext;
        }
    }

    private showError(msg: string): void {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
        this.retryBtn.hidden = false;
    }

    private updateFooterUi(): void {
        if (this.fetchError) return;
        if (!this.hasMore && this.items.length > 0) {
            this.endEl.textContent = this.endMsg;
            this.endEl.hidden = false;
        } else {
            this.endEl.hidden = true;
        }
    }
}

export function initMusicVideoList(): void {
    const root = document.querySelector<HTMLElement>("[data-mv-list]");
    if (!root) return;
    try {
        new MusicVideoList(root).init();
    } catch (e) {
        Logger.error("initMusicVideoList", e);
    }
}
